import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, scrollToHash } from "@/lib/motion";
import { useBrochureGate } from "./Growth";

const AUDIENCE = [
  {
    n: "01",
    icon: "↟",
    title: "Growers & originators",
    items: ["Farmers", "Seed Producers", "Plant Breeders", "Cooperatives", "Manufacturers", "Food Companies"],
  },
  {
    n: "02",
    icon: "↗",
    title: "Trade & market access",
    items: ["Distributors", "Importers", "Buyers", "Dealers", "Exporters", "Retailers & Retail Outlets", "Supermarkets & Department Stores", "Online Stores"],
  },
  {
    n: "03",
    icon: "✳",
    title: "Knowledge & investment",
    items: ["Healthcare Practitioners", "Researchers", "Formulators", "Consultants", "Investors", "Spa & Health Centers"],
  },
  {
    n: "04",
    icon: "◈",
    title: "Food, health & innovation",
    items: ["Nutraceuticals", "Dietary Supplements", "Functional Food & Beverages", "Plant Extracts & Botanicals", "Essential Oils & Aromatherapy", "Natural & Herbal Medicines", "Food & Beverages", "Organic Foods", "Diet Foods", "Sports Nutrition", "Spices & Spice Oils", "Health & Beauty", "Personal Care Products", "Eco Products", "Animal Nutrition", "Other Natural Products"],
  },
];

export function Participants() {
  return (
    <section id="participants" data-testid="participants-section" className="bg-[#f3f3e9] py-20 lg:py-28">
      <div className="wrap">
        <div className="grid gap-10 rounded-2xl p-8 sm:p-12 lg:grid-cols-[1.1fr_1fr]" style={{ background: "var(--forest)" }}>
          <Reveal className="text-[#f3f3e9]">
            <span className="eyebrow text-[#d7ef8b]">One ecosystem. Many perspectives.</span>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              Participants
              <br />
              <em className="italic text-[#d7ef8b]">& Presenters.</em>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed opacity-90">
              From farm to formulation.
              <br />
              From research to retail.
              <br />
              <strong>The right people. In one room.</strong>
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("participate");
              }}
              data-testid="participants-cta"
              className="ofic-btn ofic-btn-lime mt-8"
            >
              Find your place at OFIC <span className="arrow">↗</span>
            </button>
          </Reveal>
          <Reveal delay={0.1}>
            <figure className="relative overflow-hidden rounded-xl">
              <img src="/assets/conference.jpg" alt="Illustrative conference audience gathered for an industry panel" className="h-72 w-full object-cover sm:h-96" />
              <div className="absolute right-4 top-4 grid h-16 w-16 place-items-center rounded-full text-center" style={{ background: "var(--lime)" }}>
                <span className="text-[10px] font-extrabold leading-tight tracking-[0.12em] text-[#123d30]">
                  OFIC
                  <br />
                  2026
                </span>
              </div>
              <figcaption className="absolute inset-x-0 bottom-0 p-4 text-[#f3f3e9]" style={{ background: "linear-gradient(to top, rgba(11,43,33,.85), transparent)" }}>
                <span className="eyebrow text-[9px] text-[#d7ef8b]">Where perspectives meet</span>
                <strong className="mt-1 block font-display text-lg">New ideas begin with a conversation.</strong>
                <small className="text-[10px] opacity-75">Conference visual is illustrative.</small>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <Reveal className="mt-16 text-center">
          <span className="eyebrow text-[#5c6b5a]">Who you'll connect with</span>
          <p className="mt-2 font-display text-xl sm:text-2xl">A place for every part of the organic value chain.</p>
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {AUDIENCE.map((card, i) => (
            <Reveal key={card.n} delay={i * 0.06}>
              <article className="group h-full rounded-xl p-6 transition-transform duration-300 hover:-translate-y-1" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }} data-testid={`audience-card-${card.n}`}>
                <div className="flex items-start justify-between">
                  <span className="font-display text-2xl italic opacity-40">{card.icon}</span>
                  <span className="eyebrow text-[9px] text-[#5c6b5a]">{card.n} / ECOSYSTEM</span>
                </div>
                <h3 className="mt-4 font-display text-xl">{card.title}</h3>
                <ul className="mt-4 grid gap-1.5 text-[13px] text-[#3d5247]">
                  {card.items.map((item) => (
                    <li key={item} className="flex items-baseline gap-2">
                      <span className="text-[#5c6b5a]">·</span> {item}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <div className="flex flex-col items-start justify-between gap-4 rounded-xl p-6 sm:flex-row sm:items-center" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
            <div>
              <span className="eyebrow text-[#5c6b5a]">The wider conversation</span>
              <p className="mt-1.5 text-sm font-bold">Policy makers · Corporate groups · Commodity exchanges · Logistics companies · Machinery manufacturers</p>
            </div>
            <Link to="/register" data-testid="participants-wider-cta" className="text-link">
              Be part of it <span className="arrow">↗</span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const EXPERIENCES = [
  { img: "/assets/experience-conference-real.jpg", tag: "01 / LEARN", title: "Conference", copy: "Discover organic food and beverage trends, emerging technologies and the forces shaping market growth.", pos: "50% 70%" },
  { img: "/assets/experience-exhibition-real.jpg", tag: "02 / DISCOVER", title: "Exhibition", copy: "Showcase organic foods, health products, processing technology, packaging, machinery and services.", pos: "50% 60%" },
  { img: "/assets/experience-networking.jpg", tag: "03 / CONNECT", title: "Business networking", copy: "Build relationships across the value chain. Explore technology transfer, joint ventures and international trade.", pos: "50% 50%" },
  { img: "/assets/experience-recognition.jpg", tag: "04 / CELEBRATE", title: "Recognition that matters.", copy: "Explore award nomination participation and join the gala dinner on 11 December, 7:00 pm onwards.", pos: "50% 50%" },
];

export function Experiences() {
  return (
    <section id="experience" data-testid="experience-section" className="bg-[#eaeadd] py-20 lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="eyebrow text-[#5c6b5a]">02 / The OFIC experience</div>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              One platform.
              <br />
              <em className="italic">Four ways to grow.</em>
            </h2>
          </div>
          <p className="text-[15px] font-bold text-[#5c6b5a] lg:text-right">
            Ideas that inspire. Products that stand out.
            <br />
            Conversations that create possibilities.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {EXPERIENCES.map((card, i) => (
            <Reveal key={card.tag} delay={i * 0.07}>
              <article className="group flex h-full flex-col overflow-hidden rounded-xl" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }} data-testid={`experience-card-${i + 1}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={card.img} alt={`${card.title} at a Tefla's event`} loading="lazy" style={{ objectPosition: card.pos }} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="eyebrow text-[10px] text-[#5c6b5a]">{card.tag}</span>
                  <h3 className="mt-1.5 font-display text-2xl leading-snug">{card.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-[#3d5247]">{card.copy}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-[12px] text-[#5c6b5a]">
          Conference, exhibition and networking: Tefla's event highlights. Recognition: OFIC 2019 archive. The detailed programme and speakers will be announced.
        </p>
      </div>
    </section>
  );
}

const EXPO_PHOTOS = [
  ["/assets/expo-stand.jpg", "BRANDS MEET BUYERS", "Visitors connecting at a brand exhibition stand at a Tefla's event"],
  ["/assets/expo-connect.jpg", "CONVERSATIONS BECOME CONNECTIONS", "Business delegates exchanging a handshake at Globoil"],
  ["/assets/expo-floor.jpg", "IDEAS ACROSS THE AISLES", "Delegates exploring an exhibition floor at Globoil"],
  ["/assets/expo-brands.jpg", "PRODUCTS IN THE SPOTLIGHT", "Business visitors exploring product displays at Globoil"],
  ["/assets/expo-technology.jpg", "TECHNOLOGY THAT CONNECTS", "Delegates at a processing technology exhibition stand at Globoil"],
];

export function Expo() {
  const [planOpen, setPlanOpen] = useState(false);
  return (
    <section id="expo" data-testid="expo-section" className="bg-[#123d30] py-20 text-[#f3f3e9] lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="eyebrow text-[#d7ef8b]">The business of organic, on show</span>
            <h2 className="mt-4 font-display text-4xl sm:text-6xl">
              OFIC <em className="italic text-[#d7ef8b]">EXPO</em> 2026
            </h2>
          </div>
          <div className="lg:text-right">
            <p className="text-[15px] leading-relaxed opacity-90">
              Discover. Demonstrate.
              <br />
              Build your next business connection.
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("participate");
              }}
              data-testid="expo-cta"
              className="ofic-btn ofic-btn-lime mt-5"
            >
              Explore exhibition stalls <span className="arrow">↗</span>
            </button>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="expo-photo-grid">
          {EXPO_PHOTOS.map(([src, caption, alt], i) => (
            <Reveal key={src} delay={i * 0.05} className={i === 0 ? "md:col-span-2 lg:row-span-2" : ""}>
              <figure className="group relative h-full min-h-[220px] overflow-hidden rounded-xl">
                <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 text-[10px] font-extrabold tracking-[0.18em] text-[#f3f3e9]" style={{ background: "linear-gradient(to top, rgba(11,43,33,.85), transparent)" }}>
                  {caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <p className="mt-5 text-[12px] opacity-70">Exhibition and networking highlights from Tefla's Globoil events. Shown as examples of the organiser's exhibition experience.</p>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <span className="eyebrow text-[#d7ef8b]">Why exhibit at OFIC?</span>
            <h3 className="mt-3 font-display text-2xl leading-snug sm:text-4xl">
              A meeting point for products,
              <br />
              <em className="italic text-[#d7ef8b]">people and possibility.</em>
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[15px] leading-relaxed opacity-90">
              The OFIC 2026 exhibition is designed to showcase organic food and beverages, green and health food products, machinery and services. Explore emerging technologies and market trends that help businesses remain competitive.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed opacity-90">
              A platform for technology transfer, business meetings, joint ventures and potential investment — supporting production, collaboration and international trade.
            </p>
          </Reveal>
        </div>

        <Reveal className="mt-14">
          <div className="rounded-2xl p-6 sm:p-8" style={{ background: "#fbfbf4", color: "var(--forest)" }} data-testid="expo-floorplan-card">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.1fr]">
              <div>
                <span className="eyebrow text-[#5c6b5a]">Expo floor plan</span>
                <h3 className="mt-3 font-display text-2xl sm:text-4xl">
                  Plan your <em className="italic">space.</em>
                </h3>
                <p className="mt-3 max-w-md text-[14px] leading-relaxed text-[#3d5247]">
                  27 stalls across two zones at Ginger Hotel, Mumbai — with the ballroom hosting the conference and the awards gala.
                </p>
                <ul className="mt-5 grid gap-2 text-[13px] font-bold text-[#123d30]">
                  <li>· Zone A (A1–A13) — reserved for sponsors</li>
                  <li>· Zone B (B1–B14) — open exhibition</li>
                  <li>· 12 stalls of 6 sq. m (2 m × 3 m) · 15 stalls of 9 sq. m (3 m × 3 m)</li>
                  <li>· Stage 40' × 18' × 20' · Ballroom · Food courts · Two entrances</li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button onClick={() => setPlanOpen(true)} data-testid="expo-layout-open" className="ofic-btn ofic-btn-forest">
                    Open floor plan <span className="arrow">↗</span>
                  </button>
                  <a href="/assets/layout.pdf" target="_blank" rel="noreferrer" data-testid="expo-layout-download" className="ofic-btn ofic-btn-ghost">
                    Download PDF <span className="arrow">↓</span>
                  </a>
                </div>
              </div>
              <button onClick={() => setPlanOpen(true)} data-testid="expo-layout-image" aria-label="Open the expo floor plan" className="group relative block overflow-hidden rounded-xl" style={{ border: "1px solid var(--line)" }}>
                <img src="/assets/expo-layout.png" alt="OFIC 2026 expo floor plan — stall zones A and B, stage, ballroom and food courts at Ginger Hotel, Mumbai" loading="lazy" className="h-72 w-full bg-white object-cover object-top sm:h-96" />
                <span className="absolute inset-0 grid place-items-center text-[#f3f3e9] opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: "rgba(11,43,33,.4)" }}>
                  <strong className="font-display text-xl">Open floor plan ↗</strong>
                </span>
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      {planOpen && (
        <motion.div data-testid="expo-layout-lightbox" className="fixed inset-0 z-[70] overflow-y-auto bg-[#0b2b21]/85 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={() => setPlanOpen(false)}>
          <div className="mx-auto my-8 max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between text-[#f3f3e9]">
              <h3 className="font-display text-xl">OFIC 2026 — expo floor plan</h3>
              <button data-testid="expo-layout-close" aria-label="Close floor plan" onClick={() => setPlanOpen(false)} className="text-2xl leading-none">✕</button>
            </div>
            <img src="/assets/expo-layout.png" alt="Full OFIC 2026 expo floor plan with stall zones, stage, ballroom and food courts" className="w-full rounded-xl bg-white" />
            <p className="mt-3 text-center text-sm text-[#f3f3e9] opacity-80">
              Zones A & B · 27 stalls · Ginger Hotel, Mumbai · Stall allocation is subject to organiser confirmation ·{" "}
              <a href="/assets/layout.pdf" target="_blank" rel="noreferrer" className="text-[#d7ef8b] underline">Download the PDF</a>
            </p>
          </div>
        </motion.div>
      )}
    </section>
  );
}

const SECTORS = [
  { n: "01", symbol: "◒", small: "FROM FARM TO SHELF", title: "Food & beverages", items: ["Natural & organic foods", "Fruits, vegetables & dairy products", "Readymade meals & dry commodities", "Mineral water & beverages", "Refrigerated / frozen foods", "Marine & other products", "Superfoods & fortified foods"] },
  { n: "02", symbol: "✳", small: "NOURISH. RESTORE. THRIVE.", title: "Nutrition & wellness", items: ["Health & dietary supplements", "Diet products & sports food", "Nutritional drinks & antioxidants", "Vitamins & mineral supplements", "Nutraceuticals & functional food", "Natural medicinal products", "Natural remedies & therapies", "Health spas / resorts & holistic health"] },
  { n: "03", symbol: "❋", small: "NATURE, IN EVERYDAY LIFE", title: "Beauty & natural living", items: ["Natural & organic cosmetics", "Skin, hair, dental & baby care", "Fragrances, perfumes & body care", "Hygiene, massage & spa products", "Essential oils & aromatherapy", "Household cleaners", "Bio-energetics & essence sticks"] },
  { n: "04", symbol: "⌘", small: "SMARTER WAYS TO GROW", title: "Technology & services", items: ["Food, beverage & medicine processing", "Processing equipment & technology", "Specialized packaging & labelling", "Encapsulation equipment", "Bakery ingredients", "Pesticides, fertilizers & crop care", "Universities & institutes"] },
];

export function Exhibitors() {
  const brochure = useBrochureGate();
  return (
    <section id="exhibitors" data-testid="exhibitors-section" className="bg-[#f3f3e9] py-20 lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-6 lg:grid-cols-[1fr_1fr]">
          <div>
            <div className="eyebrow text-[#5c6b5a]">Four sectors. One organic ecosystem.</div>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              Exhibitor <em className="italic">Profile.</em>
            </h2>
          </div>
          <div className="lg:text-right">
            <p className="text-[15px] font-bold text-[#5c6b5a]">
              Explore the products, expertise and innovations
              <br />
              coming together across the organic value chain.
            </p>
            <button onClick={brochure} data-testid="exhibitors-brochure-link" className="text-link mt-3">
              Explore the brochure <span className="arrow">↗</span>
            </button>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {SECTORS.map((sector, i) => (
            <Reveal key={sector.n} delay={i * 0.06}>
              <article className="flex h-full flex-col rounded-xl p-6 transition-transform duration-300 hover:-translate-y-1" style={{ background: i % 2 ? "#fbfbf4" : "#fffef2", border: "1px solid var(--line)" }} data-testid={`sector-card-${sector.n}`}>
                <div className="flex items-start justify-between">
                  <span className="eyebrow text-[9px] text-[#5c6b5a]">{sector.n} / EXHIBITOR SECTOR</span>
                  <span className="font-display text-2xl italic opacity-40">{sector.symbol}</span>
                </div>
                <small className="eyebrow mt-6 text-[9px] text-[#5c6b5a]">{sector.small}</small>
                <h3 className="mt-1 font-display text-2xl">{sector.title}</h3>
                <ul className="mt-4 grid flex-1 gap-1.5 text-[13px] text-[#3d5247]">
                  {sector.items.map((item) => (
                    <li key={item} className="flex items-baseline gap-2">
                      <span className="text-[#5c6b5a]">·</span> {item}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <div className="flex flex-col items-start justify-between gap-4 rounded-xl p-7 sm:flex-row sm:items-center" style={{ background: "var(--forest)" }}>
            <div className="text-[#f3f3e9]">
              <span className="eyebrow text-[#d7ef8b]">Make your business part of the conversation</span>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl">Showcase what's next.</h3>
            </div>
            <Link to="/register?package=stall6" data-testid="exhibitors-cta" className="ofic-btn ofic-btn-lime">
              Explore exhibition stalls <span className="arrow">↗</span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
